import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, Loader2 } from "lucide-react";
import { apiFetch, saveAuthToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function LoginPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [noticeText, setNoticeText] = useState("");

  const login = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setErrorText("Inserisci email e password.");
      return;
    }
    setLoading(true);
    setErrorText("");
    setNoticeText("");
    try {
      const result = await apiFetch("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), password }),
      });
      saveAuthToken(result.token);
      const me = await refresh();
      // Admin separato: va al pannello, non alla Home operativa
      navigate(me?.is_admin ? "/admin" : "/", { replace: true });
    } catch (error) {
      setErrorText(error.message || "Controlla le credenziali.");
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async () => {
    setErrorText("");
    setNoticeText("");
    if (!email.trim()) {
      setErrorText("Inserisci la tua email, così potremo verificare la richiesta.");
      return;
    }
    try {
      const origin = window.location.origin;
      const result = await apiFetch("/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), origin }),
      });
      setNoticeText(result.message);
    } catch (error) {
      setErrorText(error.message || "Riprova più tardi.");
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="mx-auto flex w-full max-w-lg flex-1 flex-col px-6 py-6">
        {/* Hero */}
        <div className="relative -mx-6 h-52 overflow-hidden rounded-b-3xl shadow-lg">
          <img src="/login-hero.jpg" alt="MyCargoFlow" className="h-full w-full object-cover" />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, rgba(250,246,239,0) 32%, rgba(250,246,239,0.78) 70%, #FAF6EF 100%)",
            }}
          />
          <div className="absolute inset-x-0 bottom-3 text-center">
            <p className="text-3xl font-extrabold tracking-tight text-primary">
              MyCargo<span className="text-[hsl(var(--leaf))]">Flow</span>
            </p>
            <p className="mt-0.5 text-xs font-semibold text-card-foreground">
              Più carichi. Meno vuoti. Più futuro.
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={login} className="mt-6 flex flex-col gap-2">
          <label className="mt-1 text-sm font-semibold text-card-foreground">Email</label>
          <div className="flex min-h-[50px] items-center gap-2.5 rounded-2xl border border-border bg-card px-4">
            <Mail className="h-5 w-5 text-muted-foreground" />
            <input
              data-testid="login-email"
              type="email"
              autoCapitalize="none"
              placeholder="nome@azienda.it"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 flex-1 bg-transparent text-base text-foreground outline-none"
            />
          </div>

          <label className="mt-1 text-sm font-semibold text-card-foreground">Password</label>
          <div className="flex min-h-[50px] items-center gap-2.5 rounded-2xl border border-border bg-card px-4">
            <Lock className="h-5 w-5 text-muted-foreground" />
            <input
              data-testid="login-password"
              type="password"
              placeholder="La tua password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 flex-1 bg-transparent text-base text-foreground outline-none"
            />
          </div>

          <button
            type="button"
            onClick={forgotPassword}
            className="self-end py-1.5 text-sm font-bold text-primary"
          >
            Password dimenticata?
          </button>

          {errorText ? (
            <p data-testid="login-error-text" className="text-sm font-semibold text-destructive">
              {errorText}
            </p>
          ) : null}
          {noticeText ? (
            <p data-testid="login-notice-text" className="text-sm font-semibold text-[hsl(var(--success))]">
              {noticeText}
            </p>
          ) : null}

          <button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="mt-3 flex min-h-[52px] items-center justify-center gap-2 rounded-2xl bg-primary text-sm font-extrabold tracking-wide text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
            {loading ? "Accesso..." : "ACCEDI"}
          </button>
        </form>

        <div className="mt-auto flex items-center justify-center gap-1 py-4">
          <span className="text-sm text-muted-foreground">Non hai ancora un account?</span>
          <Link to="/register" className="text-sm font-bold text-primary">
            Registrati
          </Link>
        </div>
      </div>
    </div>
  );
}
